/**
 * Sword materials, every tone taken from the master palette's ramps (render/truepx/palette.ts `MASTER`: ink,
 * maroon→red, warm white, dark brown, gold/brass, jade, violet, ochre, steel blue-grey, navy, trim gold, steel→
 * off-white). Ramps run glint, light, mid, dark.
 */
import { BODIES, METALS, type Ramp, SILKS, STEELS } from './parts';

const add = (m: Map<string, Ramp>, list: Record<string, Ramp>) => {
  for (const [k, v] of Object.entries(list)) m.set(k, v);
};

// Blade steels.
add(STEELS, {
  iron: ['#c8d2dc', '#9aa8b8', '#708094', '#4e5c70'],
  steel: ['#efe7d6', '#c8d2dc', '#9aa8b8', '#626c84'],
  bright: ['#f9ebe7', '#dcd5c6', '#b4b8bc', '#84919f'],
  blue: ['#c8d2dc', '#6a86aa', '#425c82', '#2b4169'],
  dark: ['#84707c', '#4e4250', '#3b3038', '#2a2026'],
  whiteJade: ['#f9ebe7', '#eddfda', '#ceb8b8', '#ab979e'],
  crimson: ['#f48a6c', '#e55546', '#c4272d', '#671f2c'],
  bronze: ['#f6dca0', '#e4b575', '#c08c48', '#9a6732'],
  jade: ['#a4d4bc', '#6cae98', '#42897c', '#2a6660'],
  violet: ['#c8b0d6', '#9a80b4', '#725894', '#503a70'],
  gold: ['#fff2b0', '#ecd870', '#d0b440', '#a88a2c'],
});

// Fittings: guard, ferrules, pommel, locket, bands, chape.
add(METALS, {
  iron: ['#84919f', '#626c84', '#414a65', '#2a2e40'],
  blackIron: ['#626c84', '#414a65', '#2a2e40', '#1e2430'],
  bronze: ['#e4b575', '#c08c48', '#9a6732', '#6a4622'],
  brass: ['#f6dca0', '#e4b575', '#c08c48', '#9a6732'],
  gold: ['#fbeaa8', '#f0d27a', '#d6a640', '#9a7430'],
  silver: ['#efe7d6', '#dcd5c6', '#b4b8bc', '#84919f'],
  jade: ['#a4d4bc', '#6cae98', '#42897c', '#2a6660'],
  whiteJade: ['#f9ebe7', '#eddfda', '#e0cbc9', '#ab979e'],
  cinnabar: ['#f48a6c', '#e55546', '#c4272d', '#87232a'],
  violet: ['#c8b0d6', '#9a80b4', '#725894', '#503a70'],
});

// Scabbard bodies (gloss, light, base, dark).
add(BODIES, {
  leather: ['#a08474', '#7c5f57', '#573b34', '#3b2522'],
  wood: ['#7c5f57', '#573b34', '#3b2522', '#2c1c1e'],
  blackLacquer: ['#4e4250', '#3b3038', '#2a2026', '#1b1315'],
  redLacquer: ['#f48a6c', '#c4272d', '#87232a', '#671f2c'],
  maroon: ['#c4272d', '#87232a', '#671f2c', '#461420'],
  navyLacquer: ['#6a86aa', '#2b4169', '#1d2a4f', '#111c3a'],
  violetLacquer: ['#9a80b4', '#503a70', '#34234e', '#1e1430'],
  ivoryRay: ['#f9ebe7', '#eddfda', '#e0cbc9', '#ab979e'],
  jadeRay: ['#a4d4bc', '#6cae98', '#42897c', '#2a6660'],
  ochre: ['#ecd870', '#d0b440', '#a88a2c', '#7a6020'],
});

// Silks (grip wraps, tassels).
add(SILKS, {
  red: ['#f48a6c', '#e55546', '#c4272d', '#671f2c'],
  maroon: ['#c4272d', '#87232a', '#671f2c', '#461420'],
  black: ['#4e4250', '#3b3038', '#2a2026', '#0e0a10'],
  white: ['#f9ebe7', '#eddfda', '#ceb8b8', '#ab979e'],
  gold: ['#fff2b0', '#ecd870', '#d0b440', '#7a6020'],
  jade: ['#a4d4bc', '#6cae98', '#42897c', '#1a4644'],
  violet: ['#c8b0d6', '#9a80b4', '#725894', '#34234e'],
  navy: ['#6a86aa', '#425c82', '#2b4169', '#111c3a'],
  brown: ['#a08474', '#7c5f57', '#573b34', '#2c1c1e'],
  ivory: ['#f9ebe7', '#eddfda', '#e0cbc9', '#ab979e'],
});

/** Stones set in a guard or pommel: one tone and its glint. */
export const GEMS: Record<string, readonly [string, string]> = {
  ruby: ['#e55546', '#f48a6c'],
  jade: ['#42897c', '#a4d4bc'],
  sapphire: ['#425c82', '#c8d2dc'],
  amber: ['#d0b440', '#fff2b0'],
  amethyst: ['#725894', '#c8b0d6'],
  pearl: ['#e0cbc9', '#f9ebe7'],
  jet: ['#2a2026', '#84707c'],
};
